import { createClient } from '@/lib/supabase/client';

export interface Region {
  id: number;
  name: string;
  slug: string;
}

export interface Province {
  id: number;
  region_id: number;
  name: string;
  slug: string;
  plate_code: number;
  latitude?: number;
  longitude?: number;
}

export interface District {
  id: number;
  province_id: number;
  name: string;
  slug: string;
}

export interface Neighborhood {
  id: number;
  district_id: number;
  name: string;
  slug: string;
}

export async function getRegions(): Promise<Region[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('regions')
    .select('id, name, slug')
    .order('name');
  if (error) return [];
  return data || [];
}

export async function getProvinces(regionId?: number): Promise<Province[]> {
  const supabase = createClient();
  let query = supabase
    .from('provinces')
    .select('id, region_id, name, slug, plate_code, latitude, longitude')
    .order('name');
  if (regionId) {
    query = query.eq('region_id', regionId);
  }
  const { data, error } = await query;
  if (error) return [];
  return data || [];
}

export async function getDistricts(provinceId: number): Promise<District[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('districts')
    .select('id, province_id, name, slug')
    .eq('province_id', provinceId)
    .order('name');
  if (error) return [];
  return data || [];
}

export async function getNeighborhoods(districtId: number): Promise<Neighborhood[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('neighborhoods')
    .select('id, district_id, name, slug')
    .eq('district_id', districtId)
    .order('name');
  if (error) return [];
  return data || [];
}

export async function searchLocations(query: string): Promise<{
  provinces: Province[];
  districts: (District & { province_name: string })[];
  neighborhoods: (Neighborhood & { district_name: string; province_name: string })[];
}> {
  const supabase = createClient();
  const q = query.trim();
  if (!q) return { provinces: [], districts: [], neighborhoods: [] };

  const [provincesRes, districtsRes, neighborhoodsRes] = await Promise.all([
    supabase
      .from('provinces')
      .select('id, region_id, name, slug, plate_code')
      .ilike('name', `%${q}%`)
      .limit(5),
    supabase
      .from('districts')
      .select('id, province_id, name, slug, provinces(name)')
      .ilike('name', `%${q}%`)
      .limit(8),
    supabase
      .from('neighborhoods')
      .select('id, district_id, name, slug, districts(name, provinces(name))')
      .ilike('name', `%${q}%`)
      .limit(8),
  ]);

  const provinces = (provincesRes.data || []) as Province[];

  const districts = (districtsRes.data || []).map((d: any) => ({
    id: d.id,
    province_id: d.province_id,
    name: d.name,
    slug: d.slug,
    province_name: d.provinces?.name || '',
  }));

  const neighborhoods = (neighborhoodsRes.data || []).map((n: any) => ({
    id: n.id,
    district_id: n.district_id,
    name: n.name,
    slug: n.slug,
    district_name: n.districts?.name || '',
    province_name: n.districts?.provinces?.name || '',
  }));

  return { provinces, districts, neighborhoods };
}
